import { useState, useRef, useCallback } from 'react';
import type { RunResult } from '../pipeline.js';
import { t, type Lang } from '../i18n.js';
import { DrawingBar } from './DrawingBar.js';

/**
 * The overlay, drawn over the rendered page.
 *
 * The backdrop is the PDF as a person sees it. The overlay on top is drawn
 * from the *structured output* — not from the image — so what is displayed is
 * what the extractor believes it found. Where the two disagree, the reviewer
 * can see it, which is the only reason an overlay is worth producing.
 */
export function OverlayViewer({
  result,
  lang,
  sourceName,
  onCalibrate,
  onLoadAnother,
}: {
  result: RunResult;
  lang: Lang;
  /** The file this was read from, or null for a drawing that arrived without one. */
  sourceName: string | null;
  onCalibrate: (pdfDistance: number, realMm: number) => void;
  onLoadAnother: () => void;
}): JSX.Element {
  const [page, setPage] = useState(0);
  const [showOverlay, setShowOverlay] = useState(true);
  const [showIssues, setShowIssues] = useState(true);
  const [points, setPoints] = useState<{ x: number; y: number }[]>([]);
  const [realMm, setRealMm] = useState('100');
  const [view, setView] = useState({ zoom: 1, x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const dragged = useRef(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const s = (k: Parameters<typeof t>[0]): string => t(k, lang);
  const current = result.document.pages[page];
  const svg = result.artefacts.overlays[page] ?? '';
  const image = result.pageImages[page] ?? '';

  const onClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!current || !boxRef.current) return;
      // A pan ends in a click. Calibrating on it would take two points the
      // operator never chose and turn them into the drawing's scale.
      if (dragged.current) {
        dragged.current = false;
        return;
      }
      const rect = boxRef.current.getBoundingClientRect();
      // Back to page points, so the calibration distance is measured in the same
      // space the geometry lives in — undoing the pan and the zoom on the way.
      // The canvas scales about its own centre, so the centre is the fixed point.
      const cx = rect.width / 2;
      const cy = rect.height / 2;
      const lx = cx + (event.clientX - rect.left - view.x - cx) / view.zoom;
      const ly = cy + (event.clientY - rect.top - view.y - cy) / view.zoom;
      const x = (lx / rect.width) * current.widthPt;
      const y = (ly / rect.height) * current.heightPt;
      setPoints((prev) => (prev.length >= 2 ? [{ x, y }] : [...prev, { x, y }]));
    },
    [current, view],
  );

  const distance =
    points.length === 2
      ? Math.hypot(points[1]!.x - points[0]!.x, points[1]!.y - points[0]!.y)
      : 0;

  return (
    <section className="viewer">
      <div className="viewer-bar">
        <DrawingBar name={sourceName} lang={lang} onLoadAnother={onLoadAnother} />
        {result.document.pages.length > 1 && (
          <div className="pages">
            {result.document.pages.map((p, i) => (
              <button key={p.index} className={i === page ? 'on' : ''} onClick={() => setPage(i)}>
                {s('page')} {i + 1}
              </button>
            ))}
          </div>
        )}
        <label className="toggle">
          <input
            type="checkbox"
            checked={showOverlay}
            onChange={(e) => setShowOverlay(e.target.checked)}
          />
          {s('showOverlay')}
        </label>
        <label className="toggle">
          <input
            type="checkbox"
            checked={showIssues}
            onChange={(e) => setShowIssues(e.target.checked)}
          />
          {s('showIssues')}
        </label>

        {/*
          The sheet fills its column, which is large enough to read. Zoom is for
          going closer than that — a tolerance, a thread callout — and pan follows
          from it, matching the comparison stage so the two surfaces behave alike.
        */}
        <div className="viewer-zoom">
          <button onClick={() => setView((v) => ({ ...v, zoom: Math.max(v.zoom / 1.5, 1) }))}>
            {s('zoomOut')}
          </button>
          <button onClick={() => setView((v) => ({ ...v, zoom: Math.min(v.zoom * 1.5, 12) }))}>
            {s('zoomIn')}
          </button>
          <button onClick={() => setView({ zoom: 1, x: 0, y: 0 })}>{s('resetView')}</button>
          <span className="zoom-level">{view.zoom.toFixed(2)}×</span>
        </div>
      </div>

      <div
        className={`stage${showIssues ? '' : ' hide-issues'}${view.zoom > 1 ? ' pannable' : ''}`}
        ref={boxRef}
        onClick={onClick}
        onPointerDown={(e) => {
          if (view.zoom <= 1) return;
          drag.current = { x: e.clientX, y: e.clientY, ox: view.x, oy: view.y };
          dragged.current = false;
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          const d = drag.current;
          if (!d) return;
          if (Math.abs(e.clientX - d.x) + Math.abs(e.clientY - d.y) > 3) dragged.current = true;
          setView((v) => ({ ...v, x: d.ox + (e.clientX - d.x), y: d.oy + (e.clientY - d.y) }));
        }}
        onPointerUp={() => {
          drag.current = null;
        }}
        /*
          The cap is expressed as a max-WIDTH derived from the sheet's own ratio,
          not as a max-height. A capped height with the width fixed squashes the
          box, and a squashed box letterboxes the backdrop and the overlay
          independently — which reads as the extractor having found the geometry
          somewhere it is not.
        */
        style={{
          aspectRatio: current ? `${current.widthPt} / ${current.heightPt}` : '1 / 1.414',
          maxWidth: current
            ? `calc(var(--stage-cap) * ${current.widthPt} / ${current.heightPt})`
            : 'calc(var(--stage-cap) / 1.414)',
        }}
      >
        {/*
          One transform over both layers. Scaling the backdrop and the marks
          separately lets a half-pixel of rounding open up between them, and that
          drift reads as the extractor having found the geometry in the wrong
          place — the same rule the comparison stage follows.
        */}
        <div
          className="stage-canvas"
          style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.zoom})` }}
        >
          {image && <img src={image} alt="" className="page-image" draggable={false} />}
          {showOverlay && (
            <div className="overlay-layer" dangerouslySetInnerHTML={{ __html: svg }} />
          )}
          {current && (
            <svg className="calibration" viewBox={`0 0 ${current.widthPt} ${current.heightPt}`}>
              {points.map((p, i) => (
                <circle key={i} cx={p.x} cy={p.y} r={Math.max(current.widthPt / 180, 2)} />
              ))}
              {points.length === 2 && (
                <line x1={points[0]!.x} y1={points[0]!.y} x2={points[1]!.x} y2={points[1]!.y} />
              )}
            </svg>
          )}
        </div>
      </div>

      {points.length === 2 && (
        <div className="calibrate-bar">
          <span>
            {distance.toFixed(2)} pt ={' '}
            <input value={realMm} onChange={(e) => setRealMm(e.target.value)} inputMode="decimal" />{' '}
            {s('realDistance')}
          </span>
          <button
            className="primary"
            onClick={() => {
              const mm = Number(realMm);
              if (distance > 0 && mm > 0) onCalibrate(distance, mm);
            }}
          >
            {s('calibrate')}
          </button>
          <button onClick={() => setPoints([])}>{s('reset')}</button>
        </div>
      )}
    </section>
  );
}
